import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useVideo } from '../context/VideoContext';
import VideoCard from '../components/VideoCard';
import FilterBar from '../components/FilterBar';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiZap, FiArrowDown } = FiIcons;

const INITIAL_FILTERS = { industry: '', videoType: '', search: '' };

const Home = () => {
  const { videos, loading, setActiveVideoId } = useVideo();
  const navigate = useNavigate();
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(30);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const liveCount = videos.length;

  // Reset any playing video when landing on the home page
  useEffect(() => {
    setActiveVideoId(null);
  }, []);

  useEffect(() => {
    let result = [...videos];

    if (filters.industry) {
      result = result.filter(v => v.industry === filters.industry);
    }

    if (filters.videoType) {
      // video_type is stored as an array in the DB, so check with includes()
      result = result.filter(v => {
        const types = Array.isArray(v.video_type) ? v.video_type : [v.video_type];
        return types.includes(filters.videoType);
      });
    }

    if (filters.search) {
      const s = filters.search.toLowerCase();
      result = result.filter(v =>
        v.title?.toLowerCase().includes(s) ||
        v.client?.toLowerCase().includes(s) ||
        v.tags?.some(t => t.toLowerCase().includes(s))
      );
    }

    result.sort((a, b) => {
      const scoreA = parseFloat(a.market_score) || 0;
      const scoreB = parseFloat(b.market_score) || 0;
      if (scoreA !== scoreB) return scoreB - scoreA;
      return new Date(b.created_at) - new Date(a.created_at);
    });

    setFilteredVideos(result);
    setDisplayLimit(30);
  }, [filters, videos]);

  const handleLoadMore = () => setDisplayLimit(prev => prev + 30);

  const handleLogoClick = () => {
    setFilters(INITIAL_FILTERS);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
        <div className="w-12 h-12 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  const visibleVideos = filteredVideos.slice(0, displayLimit);

  return (
    <div className="min-h-screen pb-20 overflow-x-hidden relative">
      <Helmet>
        <title>Video Examples | Best Explainer Videos Curated by the Experts</title>
        <meta name="description" content="Discover a handpicked gallery of the world's most effective explainer videos. Filter by industry, style, and budget to find inspiration for your next project." />
        <meta property="og:title" content="Video Examples | Curated Explainer Video Gallery" />
        <meta property="og:description" content="The ultimate collection of high-converting explainer videos for SaaS, Fintech, and Enterprise brands." />
      </Helmet>

      <nav className="absolute top-0 right-0 p-6 z-50">
        <a
          href="https://www.whatastory.agency/resources/ai-quote?source=videoexamples.com&button=topbar"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center gap-2 px-5 py-2.5 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/20 hover:border-orange-500/50 rounded-full transition-all backdrop-blur-sm shadow-lg shadow-orange-500/5"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
          </span>
          <span className="text-xs font-black text-orange-500 uppercase tracking-widest group-hover:text-orange-400">Calculate the Price with our AI™</span>
          <SafeIcon icon={FiZap} className="text-orange-500 text-xs opacity-0 group-hover:opacity-100 -ml-2 group-hover:ml-0 transition-all" />
        </a>
      </nav>

      <main className="container mx-auto px-6 pt-24">
        <div className="max-w-4xl mx-auto mb-16 text-center">
          <motion.button
            onClick={handleLogoClick}
            className="text-6xl md:text-8xl font-black text-white mb-6 tracking-tight leading-none cursor-pointer bg-transparent border-none outline-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Video<span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-500 to-orange-600">Examples</span>
          </motion.button>
          <motion.p
            className="text-xl text-slate-400 max-w-2xl mx-auto font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Best Explainer Videos Curated by the Experts.
          </motion.p>
        </div>

        <div className="relative">
          <FilterBar filters={filters} setFilters={setFilters} />
          {liveCount > 0 && (
            <div
              className="absolute -top-3 -right-2 md:right-0 pointer-events-none select-none"
              style={{ transform: 'rotate(3deg)', zIndex: 50 }}
            >
              <span className="inline-block px-3 py-1 bg-orange-500/10 border border-orange-500/20 rounded-full text-[10px] font-black text-orange-400 uppercase tracking-widest shadow-sm">
                {liveCount} Videos Listed
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
          {visibleVideos.map((video, index) => (
            <VideoCard key={video.id} video={video} index={index} />
          ))}
        </div>

        {visibleVideos.length < filteredVideos.length && (
          <div className="flex justify-center mt-12">
            <button
              onClick={handleLoadMore}
              className="group flex items-center gap-3 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white font-bold tracking-wide transition-all hover:scale-105 active:scale-95"
            >
              Load More Videos
              <SafeIcon icon={FiArrowDown} className="text-orange-500 group-hover:translate-y-1 transition-transform" />
            </button>
          </div>
        )}

        {filteredVideos.length === 0 && (
          <div className="text-center py-40 glass-card">
            <h3 className="text-2xl font-bold text-white mb-2">No videos match your criteria</h3>
            <p className="text-slate-500">Try adjusting your filters or search terms.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Home;